import Hero from '@components/hero/Hero';
import { ContentBlock } from '@layouts/ContentBlocks';
import EmailIcon from '@mui/icons-material/Email';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import Faq, { FaqProps } from '@ui/components/faq/Faq';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import theme from '@ui/theme';
import makeStyles from '@ui/utils/makeStyles';
import properCase from '@utils/properCase';
import { makeSeoData } from 'config/next-seo.config';
import Head from 'next/head';
import { FAQPageJsonLd, NextSeo } from 'next-seo';
import React, { useEffect, useState } from 'react';
import ReactDOMServer from 'react-dom/server';

const title = 'Support | Guest House';
const description = 'We\'re here to help';

type HelpSection =
  | 'contact our team'
  | 'booking a staging'
  | 'billing'
  | 'rescheduling or canceling'
  | 'accessing our platform'
  | 'questions about a current staging?'
  | 'have more questions?';

const helpSections: HelpSection[] = [
  'contact our team',
  'booking a staging',
  'billing',
  'rescheduling or canceling',
  'accessing our platform',
  'questions about a current staging?',
  'have more questions?',
];
const defaultSection: HelpSection = 'contact our team';

const getSectionFromHash = (): HelpSection => {
  if (typeof window === 'undefined') {
    return 'contact our team';
  }

  return helpSections.includes(
    window.location?.hash.replace('#', '') as HelpSection
  )
    ? (window.location?.hash.replace('#', '') as HelpSection)
    : (defaultSection as HelpSection);
};

const faqSections: Record<HelpSection, Pick<FaqProps, 'q' | 'a'>[]> = {
  'contact our team': [
    {
      q: 'Contact',
      a: <></>,
    },
  ],
  'booking a staging': [
    {
      q: 'What areas do you serve?',
      a: (
        <>
          - Colorado: Denver, Boulder areas and surrounding cities
          <br />
          - Southern California: San Diego, Orange County, Temecula and surrounding cities          
          <br />
          - Arizona: Phoenix, Scottsdale areas and surrounding cities
          <br />
          - We do offer extended staging packages for inquiries outside of the listed cities in each market but additional fees may apply
        </>
      ),
    },
    {
      q: 'What\'s your lead-time?',
      a: (
        'Our typical lead time is 7-10 business days. Need to list sooner? Our team will do our best to accomodate a shorter lead time based on our availablity.'
      ),
    },
    {
      q: 'Do you offer other services to get homes ready for market?',
      a: (
        'Yes! We make it easy to book cleaning, photos and other services, along with your staging in one place.'
      ),
    },
    {
      q: 'What type of homes do you stage?',
      a: (
        'We stage all types of homes: single family homes, condos, townhomes and apartments.'
      ),
    },
    {
      q: 'Do you stage occupied listings?',
      a: (
        <>
          We do stage occupied listings, however additional fees and restrictions will apply as outline in our 
          {' '}
          <Link
            href="/staging-agreement"
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: 'underline', color: theme.palette.common.black }}
          >
            Staging Agreement
          </Link>
          .
        </>
      ),
    },
    {
      q: 'Can we tour the home before I book?',
      a: (
        'If you\'d like to schedule a tour before you book, we do charge a $250 deposit that will go toward your final staging costs. '
      ),
    },
    {
      q: 'How do I book a staging?',
      a: (
        'To book a staging with Guest House, you can simply fill out our instant quote form at guesthouseshop.com/homestaging where you\'ll enter your contact information and details about your listing. We will be in touch to review and discuss the project with you and make any necessary adjustments to your instant quote such as install dates or scope changes. You can then follow the checkout flow on your quote page to purchase your staging package and reserve a date.'
      ),
    },
    {
      q: 'Why should I stage?',
      a: (
        <>
          Quality Staging highlights your home&apos;s best features and distracts from less desirable ones, increasing its perceived value leading to a higher sales price and will statistically sell 11 times faster than non-staged competition. At Guest House, our focus is on creating an emotional connection during a buyer&apos;s critical first impression through using quality, on-trend furnishings that create an inviting environment. Visit our 
          {' '}
          <Link
            href="https://www.instagram.com/guesthouseshop/"
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: 'underline', color: theme.palette.common.black }}
          >
            Instagram
          </Link>
          {' '}
          to view our portfolio of work and see for yourself!
        </>
      ),
    },
  ],
  billing: [
    {
      q: 'When and how am I charged?',
      a: 'Guest House requires payment at the time of booking. You have the option of using a credit card, debit card, ACH or Affirm financing.',
    },
    {
      q: 'How long is my staging renewal?',
      a: 'After an initial 30 days, your recurring renewal begins. The card on file will be charged twice monthly until your staging de-install (which must be scheduled 5 business days in advance of the desired date).',
    },
    {
      q: 'Are there additional fees based on the location of the listing?',
      a: 'If your listing is located outside our standard service area, additional fees may apply.',
    },
    {
      q: 'Can payments be split?',
      a: 'Yes. If you wish to split your payment with another party, please mention this to your account manager and we can separate the quotes accordingly.',
    },
    {
      q: 'What happens if a staging item is damaged while in my possesion?',
      a: 'All items are assessed upon entry and exit of each home. If damages occur while inventory is staged, you will be charged the full retail price of the item.',
    },
  
  ],
  'rescheduling or canceling': [
    {
      q: 'What\'s your rescheduling policy?',
      a: (
        <>
          Requests to reschedule an install or de-install must be made at least 3 business day in advance. If you need to push an install date more than 3 weeks, we ask that you cancel and re-book the staging. A $750 restocking fee may apply to installs or de-installs scheduled with less than 3 days notice. 
          <br />
          <br />
          The Client and Guest House both reserve the right to reschedule an install or de-install where a National Weather Service Severe Weather Advisory has been issued. In such event, weather cancellations can be made at least 24 hours in advance by Client without incurring restocking fee. Please ensure all driveways and walkways are safely accessible by Guest House, and removed of snow and ice. If Guest House cannot access the property, we will need to reschedule the install or de-install the day-of and a restocking fee may apply.
        </>
      ),
    },
    {
      q: 'What\'s your cancellation policy?',
      a: 'Cancellation requests for an install, de-install or any service booked require 5 days notice in order to recieve a full refund.',
    },
    {
      q: 'Who do I contact to reschedule or cancel?',
      a: (
        <>
          Please call the General Manager in your market to reschedule or cancel a booked staging or service. You can find your GM&apos;s contact info on our 
          {' '}
          <Link
            href="/support"
            rel="noreferrer"
            style={{ textDecoration: 'underline', color: theme.palette.common.black }}
          >
            Support Page
          </Link>
          .
        </>
      ),
    },
  ],
  'accessing our platform': [
    {
      q: '',
      a: 'After booking with Guest House, you gain access to our one-of-a-kind Platform. You\'ll use the credentials created at check-out to login and track your home\'s timeline, view messages and moodboards, and download photos when they\'re ready. Please note this is separate from our website and has it\'s own URL.',
    },
  ],
  'questions about a current staging?': [
    {
      q: 'Can I add more rooms to my staging after booking?',
      a: (
        <>
          Yes! You can add rooms to your staging up to 3 business days prior to install by messaging your Designer in the platform or contacting your market team through the email or phone number listed on our 
          {' '}
          <Link
            href="/support"
            rel="noreferrer"
            style={{ textDecoration: 'underline', color: theme.palette.common.black }}
          >
            Support Page
          </Link>
          .
        </>
      ),
    },
    {
      q: 'Who is my main contact at GH once I\'ve booked?',
      a: (
        <>
          Once you&apos;ve booked, the General Manager in your market is your local contact for all inquiries, questions or concerns about your home. You can find your GM&apos;s contact info on our 
          {' '}
          <Link
            href="/support"
            rel="noreferrer"
            style={{ textDecoration: 'underline', color: theme.palette.common.black }}
          >
            Support Page
          </Link>
          .
        </>
      ),
    },
    {
      q: 'When and how can I share specific design requests?',
      a: 'Please share all design requests upfront during the kick-off call or initial walkthrough and we will do our best to accomodate based on our inventory availablity.  Additional fees may apply for any changes after installation.',
    },
    {
      q: 'How should I prepare for Install?',
      a: 'Please confirm access information provided is valid. Be sure the home is clear of any items that will not be used in staging. The property should be clean and all interior construction complete and ready for staging.',
    },
    {
      q: 'Should I be present during install?',
      a: 'You do not need to be present during the install. In fact, we ask that the home is vacant during our install so our design team can efficiently stage the home. Installs typically take 4-6 hours.',
    },
    {
      q: 'When and how do I receive the photography?',
      a: 'All assets will typically be ready within 2 business days after the shoot. Photos will be readily available to download from the staging platform and you\'ll receive email and text notifications if enabled.',
    },
    {
      q: 'Are the staging furnishings for sale?',
      a: (
        <>
          Yes! Many of the items you see used in our staging can be purchased new through Guest House and shipped to your address. In some cases, the staging furniture on-site can be purchased and left begind at de-install. Love our look and want to work with a Designer to customozie your space? Simply 
          {' '}
          <Link
            href="/home-buyer"
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: 'underline', color: theme.palette.common.black }}
          >
            submit an inquiry on our site
          </Link>
          {' '}
          to be paired with a Designer for a Design Consultaion.
        </>
      ),
    },
    {
      q: 'How do I schedule a de-install? ',
      a: (
        <>
          To schedule a de-install, you can simply request one in the staging platform or contact the general manager in your market. You can find your GM&apos;s contact info on our 
          {' '}
          <Link
            href="/support"
            rel="noreferrer"
            style={{ textDecoration: 'underline', color: theme.palette.common.black }}
          >
            Support Page
          </Link>
          .
        </>
      ),
    },
  ],
  'have more questions?': [
    {
      q: 'Who can I talk to for general staging inquiries? ',
      a: '',
    },
  ],
};

const useStyles = makeStyles()((theme) => {
  return {
    sectionList: {
      listStyleType: 'none',
      margin: 0,
      padding: 0,
    },
    sectionListItem: {
      margin: theme.spacing(2.5, 0),
      '& a': {
        color: theme.palette.text.secondary,
        fontSize: 18,
        textDecoration: 'none',
        '&:hover': {
          color: theme.palette.common.black,
        },
      },
    },
    sectionListItemActive: {
      '& a': {
        textDecoration: 'underline',
        color: theme.palette.common.black,
      },
    },
    appClipContainer: {
      display: 'none',
      backgroundColor: '#e5e8eb',
      [theme.breakpoints.up('sm')]: {
        display: 'block',
      },
    },
    formContainer: {
      padding: theme.spacing(5, 3),
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(15, 5),
      },
    },
  };
});

const LocationContactInfo: React.FC<{
  title: string;
  phone: string;
  email: string;
  hours: string;
}> = ({ title, phone, email, hours }) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h3"
        sx={{ mb: 2 }}
      >
        {title}
      </Typography>
      <Grid
        container
        sx={{
          gap: 2,
          mb: 2,
          flexWrap: 'nowrap',
        }}
      >
        <Grid
          item
          sx={{ display: 'flex', alignItems: 'start' }}
        >
          <LocalPhoneIcon />
        </Grid>
        <Grid item>
          <Typography variant="body1">
            <Box
              component="span"
              fontWeight="bold"
            >
              Call us:
            </Box>
            {' '}
            <a
              href={`tel:${phone}`}
              style={{ textDecoration: 'underline', color: theme.palette.common.black }}
            >
              {phone}
            </a>
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontStyle: 'italic' }}
          >
            Market hours: 
            {' '}
            {hours}
          </Typography>
        </Grid>
      </Grid>
      <Grid
        container
        sx={{
          gap: 2,
          mb: 2,
        }}
      >
        <Grid item>
          <EmailIcon />
        </Grid>
        <Grid item>
          <Typography variant="body1">
            <Box
              component="span"
              fontWeight="bold"
              sx={{ width: '50px' }}
            >
              Email:
            </Box>
            {' '}
            <a
              href={`mailto:${email}`}
              style={{ textDecoration: 'underline', color: theme.palette.common.black }}
            >
              {email}
            </a>
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

const ContactInfo = () => {
  return (
    <>
      <LocationContactInfo 
        title="Colorado"
        phone="720-255-1683"
        email="colorado@guesthouseshop.com"
        hours="Monday - Friday, 9am-5pm Mountain Time"
      />

      <LocationContactInfo 
        title="California: San Diego, Orange County & Los Angeles"
        phone="720-251-4499"
        email="california@guesthouseshop.com"
        hours="Monday - Friday, 8am to 4pm Pacific Time"
      />

      <LocationContactInfo 
        title="Arizona: Phoenix/Scottsdale"
        phone="602-492-2317"
        email="arizona@guesthouseshop.com"
        hours="Monday - Friday, 9am-5pm Mountain Time"
      />
    </>
  );
};

export default function Support() {
  const { theme } = useStyles();
  const { sm, lg } = useBreakpoints();
  const { classes, cx } = useStyles();

  const [activeSection, setActiveSection] =
    useState<HelpSection>(defaultSection);

  useEffect(() => {
    const sectionFromHash = getSectionFromHash();

    if (sectionFromHash !== activeSection) {
      setActiveSection(sectionFromHash);
    }
  }, []);


  return (
    <>
      <Head>
        <title>
          {title}
        </title>
      </Head>

      <NextSeo
        {...makeSeoData({
          pageTitle: title,
          metaTitle: title,
          metaDescription: description,
        })}
      />

      <FAQPageJsonLd
        mainEntity={[
          ...faqSections['contact our team'],
          ...faqSections['booking a staging'],
          ...faqSections['billing'],
          ...faqSections['accessing our platform'],
          ...faqSections['questions about a current staging?'],
          ...faqSections['have more questions?'],
        ].map((faq) => {
          return {
            questionName: ReactDOMServer.renderToString(faq.q),
            acceptedAnswerText: ReactDOMServer.renderToString(faq.a),
          };
        })}
      />

      <ContentBlock margin={[0, theme.spacing(3)]}>
        <Hero
          key="shop"
          backgroundColor="#ededed"
          backgroundImage={'/images/support/header.jpg'}
          style={{
            backgroundPosition: '50% 70%',
            backgroundSize: '100%',
            backgroundRepeat: 'no-repeat',
            minHeight: sm ? 400 : 'none',
          }}
        >
          <Box textAlign="center">
            <Typography
              variant="display2Alt"
              component="h1"
              style={{
                color: '#ffffff',
                textShadow: '0px 0px 20px rgba(0, 0, 0, .7)',
              }}
            >
              We&apos;re here to help
            </Typography>
          </Box>
        </Hero>
      </ContentBlock>

      <ContentBlock
        maxWidth={1200}
        margin={[2, 0, 12]}
        marginMd={[6, 0, 12]}
      >
        <Grid
          container
          spacing={4}
        >
          <Grid
            item
            xs={12}
            lg={3}
          >
            {lg ? (
              <ul className={classes.sectionList}>
                {helpSections.map((section) => {
                  return (
                    <li
                      key={section}
                      className={cx({
                        [classes.sectionListItem]: true,
                        [classes.sectionListItemActive]:
                          section === activeSection,
                      })}
                    >
                      <Link
                        href={`#${section}`}
                        onClick={() => {
                          setActiveSection(section);
                        }}
                      >
                        {properCase(section)}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <FormControl fullWidth>
                <Select
                  variant="outlined"
                  defaultValue={activeSection}
                  onChange={(e) => {
                    setActiveSection(e.target.value as HelpSection);
                  }}
                >
                  {helpSections.map((section) => {
                    return (
                      <MenuItem
                        key={section}
                        value={section}
                      >
                        {properCase(section)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            )}
          </Grid>

          <Grid
            item
            xs={12}
            lg={9}
          >
            <Typography
              variant="h2"
              style={{ marginBottom: theme.spacing(6) }}
            >
              {properCase(activeSection)}
            </Typography>

            {faqSections[activeSection].map((faq, i) => {
              if (activeSection === 'contact our team') {
                return (
                  <>
                    <Box sx={{ mb: 4 }}>
                      <Typography
                        variant="body1"
                        sx={{ fontSize: 18 }}
                      >
                        Need to reach a designer, chat with our sales team or
                        send details for your upcoming staging project? 
                        {' '}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontSize: 18, mb: 4 }}
                      >
                        It&apos;s easy to get in touch!
                        {' '}
                      </Typography>
                    </Box>

                    <ContactInfo />
                  </>
                );
              }

              if (activeSection === 'accessing our platform')  {
                return (
                  <>
                    <Typography
                      variant="body1"
                      sx={{ fontSize: 18 }}
                    >
                      After booking with Guest House, you gain access to our one-of-a-kind Platform. You&apos;ll use the credentials created at check-out to login and track your home&apos;s timeline, view messages and moodboards, and download photos when they&apos;re ready. Please note this is separate from our website and has it&apos;s own URL.
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontSize: 18 }}
                    >
                      Staging Platform - 
                      {' '}
                      <a
                        href="https://app.guesthouseshop.com"
                        target="_blank"
                        rel="noreferrer"
                        style={{ textDecoration: 'underline', color: theme.palette.common.black }}
                      >
                        app.guesthouseshop.com
                      </a>  
                    </Typography>
                  </>
                );
              }

              if (activeSection === 'have more questions?')  {
                return (
                  <Typography
                    variant="body1"
                    sx={{ fontSize: 18 }}
                  >
                    Reference Guest House&apos;s 
                    {' '}
                    <Link
                      href="/staging-agreement"
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: 'underline', color: theme.palette.common.black }}
                    >
                      Staging Agreement
                    </Link>
                    {' '}
                    for more information.
                  </Typography>
                );
              }

              return (
                <Faq
                  key={i}
                  size="small"
                  {...faq}
                />
              );
            })}
            {activeSection === 'billing' && (
              <Box
                sx={{
                  mt: 2,
                  mb: 2,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 18, fontWeight: '500' }}
                >
                  For other billing related inquiries, please email:
                  {' '}
                </Typography>
                <a
                  href="mailto:orders@guesthouseshop.com"
                  rel="noreferrer"
                  style={{ textDecoration: 'underline', color: theme.palette.common.black, fontWeight: '500', fontSize: '18px' }}
                >
                  orders@guesthouseshop.com
                </a>
              </Box>
            )}
            {activeSection === 'have more questions?' && (
              <Box
                sx={{
                  mt: 2,
                  mb: 2,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: 18, mb: 2 }}
                >
                  Who can I talk to for general staging inquiries? 
                  {' '}
                </Typography>
                <ContactInfo />
              </Box>
            )}
          </Grid>
        </Grid>
      </ContentBlock>
    </>
  );
}
